/**
 * Cross-field validation for per-type preference forms (Workflow
 * Prompt 11).
 *
 * The single-field validators in *Preferences.tsx only catch
 * "is this required field set?" — multi-field rules (the amber
 * threshold must be < the red threshold; KPI weights must sum to
 * ~1; bandwidth must be in a sane range) live here.
 *
 * Output is `Array<{ field, severity, message }>`:
 *   - `severity: 'error'` blocks the wizard's Generate button.
 *   - `severity: 'warn'` is advisory — render inline beneath the
 *     field, but don't gate submit (the user might know better
 *     than the rule).
 *
 * The wizard reads this on every render of the prefs step and
 * routes the entries into the existing required-fields warning
 * surface.
 */
import type { ReportTypeId } from '../../../config/reportTypes';
import type {
  TypedPreferencesByType,
  FeasibilityPreferences,
  EnvironmentalPreferences,
  TestFitPreferences,
  PowerAvailabilityPreferences,
  WaterResourcePreferences,
  InterconnectionPreferences,
} from '../../../types/siteRag';
import { stripDeprecatedPreferencesForType } from '../../../types/reportContractsV2';

export interface CrossFieldWarning {
  field: string;
  severity: 'warn' | 'error';
  message: string;
  /**
   * Configuration Upgrade (CU3) — optional one-click remedy. When set,
   * the wizard renders an "Apply suggestion" button next to the
   * warning chip; clicking it merges `{ [fixField]: suggestedValue }`
   * into the current prefs.
   *
   * The fix is deterministic (no LLM call) — only added for rules
   * with a clear remedy (e.g. amber threshold should be < red, so the
   * suggestion is "cap amber at red - 1").
   */
  suggestedFix?: {
    /** Field key to mutate. Often equals `field` but may differ when
     *  the offending field is not the one to change. */
    fixField: string;
    /** Concrete value to set. */
    suggestedValue: unknown;
    /** Button label shown to the user. */
    label: string;
  };
}

/* ── Per-type rules ──────────────────────────────────────────────── */

function feasibilityRules(p: FeasibilityPreferences): CrossFieldWarning[] {
  const out: CrossFieldWarning[] = [];
  // KPI weights — accept ±5% slack to keep sliders forgiving.
  // Was already advisory in the form; promote to a tracked warning.
  const w = p.kpiWeightOverrides;
  if (w) {
    const sum = Object.values(w).reduce((s, v) => s + (v ?? 0), 0);
    if (Object.values(w).some(v => typeof v === 'number')) {
      if (sum > 1.05 || sum < 0.95) {
        out.push({
          field: 'kpiWeightOverrides',
          severity: 'warn',
          message: `KPI weights sum to ${sum.toFixed(2)} — should land between 0.95 and 1.05.`,
        });
      }
    }
  }
  // SSOT — powerSourcePreference / coolingPreference live on
  // projectIntent now (nuclearInterest / coolingStrategy); this rule's
  // gating fields are no longer on FeasibilityPreferences. Removed.
  return out;
}

function powerAvailabilityRules(p: PowerAvailabilityPreferences): CrossFieldWarning[] {
  const out: CrossFieldWarning[] = [];
  const amber = p.maxSubstationDistanceMiAmber;
  const red = p.maxSubstationDistanceMiRed;
  if (typeof amber === 'number' && typeof red === 'number' && amber >= red) {
    // CU3 — deterministic remedy: cap amber at red - 1 so amber stays
    // below red. Click-to-apply.
    out.push({
      field: 'maxSubstationDistanceMiAmber',
      severity: 'warn',
      message: `Amber threshold (${amber} mi) should be less than red (${red} mi).`,
      suggestedFix: {
        fixField: 'maxSubstationDistanceMiAmber',
        suggestedValue: Math.max(0.5, red - 1),
        label: `Cap amber at ${Math.max(0.5, red - 1)} mi`,
      },
    });
  }
  // CU3 — btmStrategy=btm-only requires renewablesPreference to set
  // the BTM technology framing. Suggest 'preferred' as a sensible
  // default if the user hasn't picked one.
  if (p.btmStrategy === 'btm-only' && p.renewablesPreference === undefined) {
    out.push({
      field: 'btmStrategy',
      severity: 'warn',
      message: 'BTM-only strategy benefits from a renewables preference — without one the BTM technology framing falls through to "agnostic".',
      suggestedFix: {
        fixField: 'renewablesPreference',
        suggestedValue: 'preferred',
        label: 'Set renewables preference to Preferred',
      },
    });
  }
  return out;
}

function interconnectionRules(p: InterconnectionPreferences): CrossFieldWarning[] {
  const out: CrossFieldWarning[] = [];
  const bw = p.bandwidthGbps;
  if (typeof bw === 'number') {
    if (bw < 0.1 || bw > 10000) {
      out.push({
        field: 'bandwidthGbps',
        severity: 'error',
        message: `Bandwidth ${bw} Gbps is outside the valid range [0.1, 10000].`,
      });
    } else if (bw < 1 || bw > 1000) {
      out.push({
        field: 'bandwidthGbps',
        severity: 'warn',
        message: `Bandwidth ${bw} Gbps is unusual for a data center — typical projects sit between 1 and 1000.`,
      });
    }
  }
  // Latency targets — sanity-bound to [1, 200] ms.
  const lt = p.latencyTargetsMs;
  if (lt) {
    for (const [k, v] of Object.entries(lt) as Array<[keyof typeof lt, number | undefined]>) {
      if (typeof v === 'number' && (v < 1 || v > 200)) {
        out.push({
          field: 'latencyTargetsMs',
          severity: 'warn',
          message: `Latency target ${k}=${v}ms is outside typical range [1, 200].`,
        });
      }
    }
  }
  return out;
}

function testFitRules(p: TestFitPreferences): CrossFieldWarning[] {
  const out: CrossFieldWarning[] = [];
  if (typeof p.phaseCount === 'number' && (p.phaseCount < 1 || p.phaseCount > 10)) {
    out.push({
      field: 'phaseCount',
      severity: 'error',
      message: `Phase count ${p.phaseCount} is outside the valid range [1, 10].`,
    });
  }
  if (typeof p.targetFootprintAcres === 'number' && p.targetFootprintAcres > 1000) {
    out.push({
      field: 'targetFootprintAcres',
      severity: 'warn',
      message: `${p.targetFootprintAcres} acres is over-the-top large for a single project — confirm the unit is acres.`,
    });
  }
  return out;
}

function environmentalRules(_p: EnvironmentalPreferences): CrossFieldWarning[] {
  // Currently no inter-field rules. The 'auto' → undefined commit
  // mapping is handled by sanitizePreferencesForCommit, not warned here.
  return [];
}

function waterResourceRules(_p: WaterResourcePreferences): CrossFieldWarning[] {
  // SSOT — `coolingType` lived on WaterResourcePreferences pre-SSOT; the
  // canonical home is now projectIntent.coolingStrategy. The cross-field
  // rule that combined cooling + droughtScenario lives upstream now (it
  // belongs in a site-level Intent + Drought sanity panel, registered as
  // SSOT-FOLLOWUP if product wants to re-surface it).
  return [];
}

/* ── Dispatcher ──────────────────────────────────────────────────── */

export function crossFieldWarnings(
  reportType: ReportTypeId,
  prefs: TypedPreferencesByType[ReportTypeId] | undefined,
): CrossFieldWarning[] {
  if (!prefs) return [];
  switch (reportType) {
    case 'feasibility':         return feasibilityRules(prefs as FeasibilityPreferences);
    case 'environmental':       return environmentalRules(prefs as EnvironmentalPreferences);
    case 'test-fit':            return testFitRules(prefs as TestFitPreferences);
    case 'power-availability':  return powerAvailabilityRules(prefs as PowerAvailabilityPreferences);
    case 'water-resource':      return waterResourceRules(prefs as WaterResourcePreferences);
    case 'interconnection':     return interconnectionRules(prefs as InterconnectionPreferences);
    default:                    return [];
  }
}

/* ── Commit-time sanitization ────────────────────────────────────── */

/**
 * Apply shape fixes the prompt builder expects but the form may
 * have left in a transient UI-friendly state:
 *
 *   - Environmental.airBasinOverride === 'auto'  → undefined
 *     ('auto' means "let the SED resolve it").
 *   - Interconnection.bandwidthGbps clamped to [0.1, 10000].
 *   - TestFit.phaseCount clamped to [1, 10].
 *
 * SSOT — water-resource.coolingType sanitization removed: the field no
 * longer exists on WaterResourcePreferences.
 *
 * Pure: returns a new object; never mutates the input.
 */
export function sanitizePreferencesForCommit<T extends ReportTypeId>(
  reportType: T,
  prefs: TypedPreferencesByType[T] | undefined,
): TypedPreferencesByType[T] | undefined {
  if (!prefs) return prefs;
  const canonicalPrefs = stripDeprecatedPreferencesForType(reportType, prefs) as TypedPreferencesByType[T];
  if (reportType === 'environmental') {
    const p = canonicalPrefs as EnvironmentalPreferences;
    if (p.airBasinOverride === 'auto') {
      const { airBasinOverride: _drop, ...rest } = p;
      return rest as TypedPreferencesByType[T];
    }
    return canonicalPrefs;
  }
  if (reportType === 'interconnection') {
    const p = canonicalPrefs as InterconnectionPreferences;
    if (typeof p.bandwidthGbps === 'number') {
      const clamped = Math.max(0.1, Math.min(10000, p.bandwidthGbps));
      if (clamped !== p.bandwidthGbps) {
        return { ...p, bandwidthGbps: clamped } as TypedPreferencesByType[T];
      }
    }
    return canonicalPrefs;
  }
  if (reportType === 'test-fit') {
    const p = canonicalPrefs as TestFitPreferences;
    if (typeof p.phaseCount === 'number') {
      const clamped = Math.max(1, Math.min(10, Math.round(p.phaseCount)));
      if (clamped !== p.phaseCount) {
        return { ...p, phaseCount: clamped } as TypedPreferencesByType[T];
      }
    }
    return canonicalPrefs;
  }
  return canonicalPrefs;
}
